import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { innovationAreas, productImages } from '../data/mockData';

const Innovation = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-secondary">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <div className="inline-block mb-6">
              <span className="text-sm font-semibold text-brand-teal uppercase tracking-wider">
                Product Categories
              </span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-light leading-tight mb-6">
              Innovation Across <span className="font-medium text-brand-teal">Beauty & Personal Care</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Specialized formulation expertise spanning skincare, haircare, personal care, and cosmeceuticals. 
              Whatever your product vision, we have the scientific capability to bring it to life.
            </p>
          </div>
        </div>
      </section>

      {/* Innovation Areas */}
      <section className="py-24">
        <div className="container mx-auto px-6 lg:px-12">
          {innovationAreas.map((area, index) => (
            <div
              key={area.id}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-24 ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={area.image}
                    alt={area.category}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
              </div>
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <div className="inline-block mb-4">
                  <span className="text-xs font-semibold text-brand-teal uppercase tracking-wider">
                    Category {String(area.id).padStart(2, '0')}
                  </span>
                </div>
                <h2 className="text-3xl lg:text-4xl font-light mb-6">
                  {area.category}
                </h2>
                <p className="text-base text-muted-foreground leading-relaxed mb-8">
                  Our {area.category.toLowerCase()} formulation expertise combines cutting-edge active ingredients 
                  with consumer-preferred textures and sensory profiles. We develop products that deliver both 
                  efficacy and delightful user experiences.
                </p>
                <div className="space-y-3 mb-8">
                  {area.products.map((product, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle2 size={20} className="text-brand-teal mt-0.5 flex-shrink-0" />
                      <span className="text-base">{product}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center text-brand-teal font-semibold hover:gap-3 transition-all"
                >
                  Request {area.category} Consultation
                  <ArrowRight size={18} className="ml-2" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Specialty Formulations */}
      <section className="py-24 bg-secondary">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light mb-4">
              Specialty <span className="font-medium text-brand-teal">Formulation Capabilities</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Advanced formulation technologies for differentiated product positioning
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Clean Beauty Formulations",
                description: "Natural, organic, and sustainable ingredient-focused products meeting clean beauty standards"
              },
              {
                title: "K-Beauty & J-Beauty Inspired",
                description: "Asian beauty trends including essence, ampoules, and multi-step skincare innovations"
              },
              {
                title: "Gender-Neutral Products",
                description: "Inclusive formulations designed for all skin types and gender identities"
              },
              {
                title: "Waterless Formulations",
                description: "Concentrated, sustainable formulas reducing water usage and environmental impact"
              },
              {
                title: "Microbiome-Friendly",
                description: "Formulations that support skin's natural microbiome for long-term health"
              },
              {
                title: "CBD & Hemp Cosmetics",
                description: "Cannabinoid-infused products for skin soothing and wellness benefits"
              }
            ].map((specialty, index) => (
              <div key={index} className="bg-background p-8 hover-lift">
                <h3 className="text-xl font-medium mb-3">{specialty.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {specialty.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto bg-foreground text-white p-12 lg:p-16">
            <div className="text-center">
              <h2 className="text-3xl lg:text-4xl font-light mb-6">
                Have a Unique Product Concept?
              </h2>
              <p className="text-lg text-white/80 mb-8 leading-relaxed max-w-2xl mx-auto">
                Our R&D team specializes in bringing unconventional ideas to life. Share your vision, 
                and we'll develop the formulation to match.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-sm font-semibold text-foreground bg-white hover:bg-white/90"
              >
                Discuss Your Product Idea
                <ArrowRight size={18} className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Innovation;
